/**
 * Hook for managing user companies and company switching
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@ui/use-toast';
import { getUserCompanies, switchCompany, getCurrentCompany } from '@lib/api/user-companies';

/**
 * Hook for managing user companies and company switching
 */
export const useUserCompanies = () => {
  const queryClient = useQueryClient();

  // Query for fetching user companies
  const {
    data: companies,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userCompanies'],
    queryFn: getUserCompanies,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation for switching companies
  const {
    mutate: switchToCompany,
    isPending: isSwitching,
    error: switchError,
  } = useMutation({
    mutationFn: switchCompany,
    onSuccess: (data: { company_id: string; company_name: string }) => {
      // Invalidate and refetch company-related queries
      queryClient.invalidateQueries({ queryKey: ['companyBranding'] });
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      queryClient.invalidateQueries({ queryKey: ['benefitTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['currentCompany'] });

      // Billing is per-company; drop the previous company's status so the new
      // company's billing page can't briefly render stale plan/invoice data.
      queryClient.removeQueries({ queryKey: ['billing'] });
      queryClient.invalidateQueries({ queryKey: ['billing'] });

      // Force refetch the current company immediately
      queryClient.refetchQueries({ queryKey: ['currentCompany'] });

      // Show success message
      toast({
        title: 'Success',
        description: `Switched to ${data.company_name}`,
      });
    },
    onError: error => {
      // Show error message
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to switch company',
        variant: 'destructive',
      });
    },
  });

  return {
    companies,
    isLoading,
    isSwitching,
    error,
    switchError,
    switchCompany: switchToCompany,
    refetch,
  };
};

/**
 * Hook for getting current active company
 */
export const useCurrentCompany = () => {
  const {
    data: currentCompany,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['currentCompany'],
    queryFn: getCurrentCompany,
    staleTime: 0, // No stale time - always refetch when invalidated
  });

  return {
    currentCompany,
    isLoading,
    error,
    refetch,
  };
};
